/**
 * Client view of permission-service's case-level CSAM flag (TR-2631).
 *
 * The two stored sources are folded server-side: ``csam_flagged`` is on when
 * either auto (findings) or sticky manual is on. Clients must not re-derive
 * that OR. ``csam_can_turn_off`` is whether ``PUT …/csam-flag`` with
 * ``flagged=false`` would succeed — so a UI can disable the toggle instead of
 * offering a button that 409s. It says nothing about whether the caller is a
 * CSAM verifier.
 *
 * Missing or non-boolean fields read as unflagged. Provenance's case list
 * never sent these keys; permission-service ``/user/cases`` and CSM
 * ``/cases`` do.
 */
export type CsamFlagSource = 'auto' | 'manual';

export type CaseCsamFlag = {
  csamFlagged: boolean;
  csamFlagSource: CsamFlagSource | null;
  csamCanTurnOff: boolean;
};

export const UNFLAGGED_CASE_CSAM: CaseCsamFlag = {
  csamFlagged: false,
  csamFlagSource: null,
  csamCanTurnOff: false,
};

export function parseCsamFlagSource(value: unknown): CsamFlagSource | null {
  return value === 'auto' || value === 'manual' ? value : null;
}

export function parseCaseCsamFlag(raw: {
  csam_flagged?: unknown;
  csam_flag_source?: unknown;
  csam_can_turn_off?: unknown;
}): CaseCsamFlag {
  const csamFlagged = raw.csam_flagged === true;
  const source = parseCsamFlagSource(raw.csam_flag_source);
  return {
    csamFlagged,
    csamFlagSource: csamFlagged ? source : null,
    csamCanTurnOff: raw.csam_can_turn_off === true,
  };
}
